import { withTheme } from 'emotion-theming'

import { useState, useEffect, useRef, useContext, useMemo } from 'react'
import styled from '@emotion/styled'
import PropTypes from 'prop-types'
import { withTranslation /* , i18n */ } from 'server/i18n'
import AppContext from 'contexts/AppContext'

import FilterIconAlt from 'public/icons/filter-alt.svg'
import CloseIcon from 'public/icons/close.svg'
import BoatsFilter from 'components/BoatsFilter'
import Button from 'components/Button'

import filters from './filters'

import style from './BoatsFilterPanel.style'

const BoatsFilterPanelStyled = styled.div(props => ({ ...style(props) }))

const BoatsFilterPanel = ({ brandList, randomTrigger, t, i18n }) => {
    const isBrowser = typeof window !== 'undefined'
    const [viewportWidth, setViewportWidth] = useState(isBrowser ? window.innerWidth : 0)
    const [opened, setMobilePanel] = useState(false)
    const [sticky, setSticky] = useState(false)
    const [activeFilter, setActiveFilter] = useState(null)
    const { filterPosition, setFilterPosition } = useContext(AppContext)
    const { numberOfFilteredBoats } = useContext(AppContext)

    const stickyFilterPanel = useRef()

    const toggleMobilePanel = () => {
        setMobilePanel(!opened)
    }

    const handleScroll = () => {
        setSticky(stickyFilterPanel.current.getBoundingClientRect().top <= 0)
    }

    const closeOtherFilters = id => {
        setActiveFilter(activeFilter === id ? null : id)
    }

    const filterList = useMemo(() => {
        return (filters || []).map(filter => {
            if (filter.filterType === 'brand') {
                return {
                    ...filter,
                    options: brandList
                }
            }
            return filter
        })
    }, [filters, brandList])

    useEffect(() => {
        if (randomTrigger) {
            toggleMobilePanel()
        }
    }, [randomTrigger])

    useEffect(() => {
        let resizeTimeout = null
        const delay = 300

        const handleResize = () => {
            clearTimeout(resizeTimeout)
            resizeTimeout = setTimeout(() => setViewportWidth(window.innerWidth), delay)
        }

        if (isBrowser) {
            // Find put position of filters so that we can scroll to it on Filter click.
            setFilterPosition(stickyFilterPanel.current.getBoundingClientRect().top + window.scrollY)
            window.addEventListener('scroll', handleScroll)
            window.addEventListener('resize', handleResize)
        }
        return () => {
            if (isBrowser) {
                window.removeEventListener('scroll', handleScroll)
                window.removeEventListener('resize', handleResize)
            }
        }
    }, [filterPosition])

    return (
        <BoatsFilterPanelStyled className={sticky ? 'isSticky' : null} ref={stickyFilterPanel}>
            <button className="PanelTrigger" type="button" onClick={() => toggleMobilePanel()}>
                <span className="text">Filters</span>
                <span className="icon">
                    <FilterIconAlt />
                </span>
            </button>

            {opened ? (
                <div className="MobilePanel">
                    <div className="MobilePanel--header">
                        <span>{t('filters')}</span>

                        <button type="button" onClick={() => toggleMobilePanel()}>
                            <CloseIcon />
                        </button>
                    </div>
                    {filterList.map(filter => (
                        <BoatsFilter
                            key={filter.id}
                            filterName={filter.filterName}
                            first={filter.isFirst}
                            options={filter.options}
                            icon={filter.icon}
                            filterType={filter.filterType}
                            isOpened={filter.id === activeFilter}
                            notifyParentAboutOpenFilter={() => closeOtherFilters(filter.id)}
                        />
                    ))}

                    <div className="MobilePanel--button">
                        <Button variation="secondary" onClick={() => toggleMobilePanel()}>
                            {i18n.language === 'en'
                                ? `Show ${numberOfFilteredBoats} ${numberOfFilteredBoats > 1 ? 'boats' : 'boat'}`
                                : ''}

                            {i18n.language === 'de'
                                ? `${
                                      numberOfFilteredBoats > 1
                                          ? `Zeige ${numberOfFilteredBoats} Boote`
                                          : '1 Boot anzeigen'
                                  }`
                                : ''}
                        </Button>
                    </div>
                </div>
            ) : null}

            <div className="DesktopPanelWrap">
                <div className="DesktopPanel">
                    {viewportWidth >= 1024 &&
                        filterList.map(filter => (
                            <BoatsFilter
                                key={filter.id}
                                filterName={filter.filterName}
                                first={filter.isFirst}
                                options={filter.options}
                                icon={filter.icon}
                                filterType={filter.filterType}
                                isOpened={filter.id === activeFilter}
                                isDesktop
                                notifyParentAboutOpenFilter={() => closeOtherFilters(filter.id)}
                            />
                        ))}
                </div>
            </div>
        </BoatsFilterPanelStyled>
    )
}

BoatsFilterPanel.propTypes = {
    brandList: PropTypes.arrayOf(PropTypes.shape({})),
    randomTrigger: PropTypes.number,
    t: PropTypes.func.isRequired,
    i18n: PropTypes.oneOfType([() => null, PropTypes.object]).isRequired
}
BoatsFilterPanel.defaultProps = {
    randomTrigger: undefined,
    brandList: []
}

export default withTranslation('common')(withTheme(BoatsFilterPanel))
